/**
 * url.ts — управление URL и history (SPA-навигация)
 * Проект: TorgiRT (shared — все витрины)
 *
 * v2: restoreStateFromUrl() больше не вызывает selectCategory/selectSubcategory
 *     (они стали async). Вместо этого восстанавливает state напрямую,
 *     а fetchProducts() вызывается отдельно из main.js / popstate.
 */

import type { Category, Subcategory } from './types';
import { state, BASE_PATH, refs } from './state';

interface FindResult {
  type: "category" | "subcategory";
  category: Category;
  subcategory?: Subcategory;
}

/**
 * Найти категорию или подкатегорию по числовому dbId.
 */
export function findByDbId(dbId: number | string): FindResult | null {
  const numId = Number(dbId);
  for (const category of state.catalogData.categories) {
    if (category.dbId === numId) {
      return { type: "category", category };
    }
    for (const subcategory of category.subcategories) {
      if (subcategory.dbId === numId) {
        return { type: "subcategory", category, subcategory };
      }
    }
  }
  return null;
}

/**
 * Получить числовой dbId для URL-параметра cat.
 */
export function getUrlCatDbId(): number | undefined | null {
  if (!state.selectedCategory) return null;
  const cat = state.catalogData.categories.find(
    (c) => c.id === state.selectedCategory,
  );
  if (!cat) return null;

  if (state.selectedSubcategory) {
    const sub = cat.subcategories.find(
      (s) => s.id === state.selectedSubcategory,
    );
    if (sub && sub.dbId !== 0) return sub.dbId;
  }

  return cat.dbId;
}

export function buildUrlFromState(): string {
  const hasSearch = state.searchQuery.trim().length > 0;
  const hasFilters = Object.keys(state.activeFilters).length > 0;

  const base = (hasSearch || hasFilters)
    ? BASE_PATH + "/search"
    : BASE_PATH;

  const params = new URLSearchParams();

  const catDbId = getUrlCatDbId();
  if (catDbId != null) {
    params.set("cat", String(catDbId));
  }
  if (state.currentPage > 1) {
    params.set("page", String(state.currentPage));
  }
  if (hasSearch) {
    params.set("q", state.searchQuery.trim());
  }
  for (const [code, value] of Object.entries(state.activeFilters)) {
    params.set("filters[" + code + "]", value);
  }
  if (state.sortBy) {
    params.set("sort", state.sortBy);
  }
  if (state.priceMin) {
    params.set("price_min", state.priceMin);
  }
  if (state.priceMax) {
    params.set("price_max", state.priceMax);
  }
  if (state.stockOnly) {
    params.set("stock", "1");
  }
  if (state.favoritesOnly) {
    params.set("fav", "1");
  }

  const qs = params.toString();
  return qs ? base + "?" + qs : base;
}

export function pushUrlState(): void {
  const url = buildUrlFromState();
  history.pushState(null, "", url);
}

export function replaceUrlState(): void {
  const url = buildUrlFromState();
  history.replaceState(null, "", url);
}

/**
 * Восстановить состояние приложения из GET-параметров URL.
 */
export function restoreStateFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const catDbId = params.get("cat");
  const page = parseInt(params.get("page") as string, 10);
  const q = params.get("q");

  if (catDbId) {
    const found = findByDbId(catDbId);
    if (found) {
      if (found.type === "subcategory") {
        state.selectedCategory = found.category.id as any;
        state.selectedSubcategory = found.subcategory!.id as any;
        state.breadcrumbPath = {
          categoryName: found.category.name,
          subcategoryName: found.subcategory!.name,
        } as any;
        state.currentFilters = [...(found.category.filters as any)];
      } else {
        state.selectedCategory = found.category.id as any;
        state.selectedSubcategory = null;
        state.breadcrumbPath = {
          categoryName: found.category.name,
          subcategoryName: "",
        } as any;
        state.currentFilters = [...(found.category.filters as any)];
      }
    }
  }

  if (q) {
    state.searchQuery = q;
    if (refs.searchInput) (refs.searchInput as HTMLInputElement).value = q;
  }

  for (const [key, value] of params.entries()) {
    if (key.startsWith("filters[") && key.endsWith("]") && value) {
      const filterCode = key.slice(8, -1);
      state.activeFilters[filterCode] = value;
    }
  }

  const sort = params.get("sort");
  if (sort === "price_asc" || sort === "price_desc") {
    state.sortBy = sort;
  }

  const priceMin = params.get("price_min");
  const priceMax = params.get("price_max");
  if (priceMin) state.priceMin = priceMin;
  if (priceMax) state.priceMax = priceMax;
  state.stockOnly = params.get("stock") === "1";
  state.favoritesOnly = params.get("fav") === "1";

  if (!isNaN(page) && page > 1) {
    state.currentPage = page;
  }
}
